import { create } from 'zustand';
import { combine, subscribeWithSelector } from 'zustand/middleware';
import { eventBus } from '../event-manager';
import { type InternalSetting } from '../types/InternalSettingSchema';
import { EVENT_LISTENER_METHODS, INTERNAL_EVENTS, tryCatch } from '../utils';

type ConnectionState = {
  connection: any;
  error: any;
  timeEstablished: number | undefined;
  jwt?: string;
  connectionConfig?: InternalSetting['settings']['connectionConfig'];
  connectionStatus:
    | 'initial'
    | 'connecting'
    | 'connected'
    | 'error'
    | 'disconnecting';
};

type Actions = {
  connectionWillConnect: (connection: any) => void;
  connectionEstablished: (connection: any) => void;
  connectionFailed: (error: any) => void;
  disconnect: () => Promise<void>;
  reset: () => void;
};

const initialState: ConnectionState = {
  connection: undefined,
  connectionStatus: 'initial',
  error: undefined,
  timeEstablished: undefined,
};

export const useConnectionStore = create<ConnectionState & Actions>()(
  subscribeWithSelector(
    combine(initialState, set => ({
      connectionEstablished: (connection: any) =>
        set({
          connection: connection,
          connectionStatus: 'connected',
          error: undefined,
          timeEstablished: Date.now(),
        }),
      connectionFailed: (error: any) =>
        set({
          connectionStatus: 'error',
          error,
          timeEstablished: undefined,
        }),
      connectionWillConnect: (connection: any) => {
        if (typeof window?.APP !== 'undefined') {
          window.APP.connection = connection;
        }
        connection.addFeature('http://jitsi.org/protocol/jibri');
        set({
          connection: connection,
          connectionStatus: 'connecting',
          error: undefined,
          timeEstablished: undefined,
        });
      },
      disconnect: async () => {
        const connection = useConnectionStore.getState().connection;
        const connectionStatus = useConnectionStore.getState().connectionStatus;
        if (connection && connectionStatus !== 'disconnecting') {
          set({
            connectionStatus: 'disconnecting',
          });
          eventBus.publish({
            type: INTERNAL_EVENTS.connectionWillClose,
          });
          eventBus.stopEmitting();
          await tryCatch(connection.disconnect());
          eventBus.publish(
            {
              type: EVENT_LISTENER_METHODS.SessionStatusListener
                .onConnectionClosed,
            },
            true,
          );
          set(initialState);
        }
      },
      reset: () => set(initialState),
    })),
  ),
);

export function waitForConnection(): Promise<void> {
  const { connectionStatus } = useConnectionStore.getState();
  if (connectionStatus === 'connected') return Promise.resolve();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsub();
      reject(new Error('Connection timed out after 3 seconds'));
    }, 3000);

    const unsub = useConnectionStore.subscribe(
      s => s.connectionStatus,
      status => {
        if (status === 'connected') {
          clearTimeout(timeout);
          unsub();
          resolve();
        } else if (status === 'error') {
          clearTimeout(timeout);
          unsub();
          reject(useConnectionStore.getState().error);
        }
      },
    );
  });
}
